import { inject } from '@angular/core';
import { CanMatchFn, Router, UrlSegment } from '@angular/router';

/**
 * Matches legacy job URLs — `job/...`, `jobs/v2/...`, `jobs/v3/...`, `jobs/v4/...`,
 * and the retired estimating page (`estimating` and `estimating/:jobId`).
 *
 * The job module was consolidated into a single de-versioned `jobs/:jobId` tree, and
 * the standalone estimating page was folded into it, so these routes no longer exist.
 * Links to them still live in the wild (emails, bookmarks, external systems), so we
 * match them here to redirect — see {@link LegacyJobRouteRedirectGuard}.
 */
export const legacyJobRouteMatcher = (segments: UrlSegment[]) => {
  const isSingularJobRoute = segments.length >= 2 && segments[0].path === 'job';
  const isVersionedJobsRoute = segments.length >= 2
    && segments[0].path === 'jobs'
    && [ 'v2', 'v3', 'v4' ].includes(segments[1].path);
  // Both the bare `estimating` route (new estimate) and `estimating/:jobId` (a specific
  // job) are matched — see the guard for how each maps onto the consolidated tree.
  const isEstimatingRoute = segments.length >= 1 && segments[0].path === 'estimating';

  if (isSingularJobRoute || isVersionedJobsRoute || isEstimatingRoute) {
    return { consumed: segments };
  }
  return null;
};

/**
 * Redirects legacy job links to their consolidated equivalent, preserving the rest
 * of the path and the query params:
 *
 *   job/:jobId              -> jobs/:jobId
 *   job/:jobId/overview     -> jobs/:jobId/overview
 *   jobs/v3/:jobId          -> jobs/:jobId
 *   jobs/v2/:jobId/overview -> jobs/:jobId/overview
 *   jobs/v4/new             -> jobs/new
 *   estimating              -> jobs/new
 *   estimating/:jobId       -> jobs/:jobId
 *
 * Used as a `canMatch` guard on a matcher route (see {@link legacyJobRouteMatcher}).
 * Returning a `UrlTree` from `canMatch` cancels the match and redirects.
 */
export const LegacyJobRouteRedirectGuard: CanMatchFn = (_route, segments: UrlSegment[]) => {
  const router = inject(Router);

  const paths = segments.map((segment) => segment.path);

  // Carry over the query params of the link being followed.
  const queryParams = router.getCurrentNavigation()?.extractedUrl.queryParams ?? {};

  // The bare `estimating` route was the entry point for creating a new estimate; it now
  // maps onto the new-job flow rather than a specific job.
  if (paths.length === 1 && paths[0] === 'estimating') {
    return router.createUrlTree([ 'jobs', 'new' ], { queryParams });
  }

  // `job/...` and `estimating/...` carry a single leading segment; the versioned
  // `jobs/vN/...` routes carry two.
  const rest = paths[0] === 'jobs' ? paths.slice(2) : paths.slice(1);

  return router.createUrlTree([ 'jobs', ...rest ], { queryParams });
};
